import { Injectable, Injector } from '@angular/core';
import { Transition, UIRouter } from '@uirouter/core';
import * as _ from 'lodash';
import moment from 'moment';

import { MESSAGECONSTANTS } from '../utils/constants';
import { AppVersionService } from './app-version.service';
import { AuthService } from './auth.service';
import { AngularModalService } from './modal.service';
import { PlanService } from './plan.service';
import { ShellService } from './shell.service';
import { StateLocationService } from './state-location.service';
import { StorageService } from './storage.service';
import { UserService } from './user.service';
import { WebSocketsService } from './web-sockets.service';

const LOGIN_NOT_REQUIRED_STATES = [
  'login',
  'setpassword',
  'resetPassword',
  'validateOtp',
  'authenticateToken',
  'forgotPassword',
  'candidateConference',
  'tokenLogin'
];

// Built inside UIRouter's provider factory (via appInitConfig). Services that inject StateService,
// or that transitively reach ApplicationRef (which Angular wires back to the APP_INITIALIZER
// array, including UI-Router's own entry), would re-enter that factory, so they are resolved
// lazily here instead of being constructor dependencies.
@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  constructor(
    private storageService: StorageService,
    private appVersionService: AppVersionService,
    private webSocketsService: WebSocketsService,
    private shellService: ShellService,
    private injector: Injector
  ) {
  }

  private get modalService(): AngularModalService {
    return this.injector.get(AngularModalService);
  }

  private get userService(): UserService {
    return this.injector.get(UserService);
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get planService(): PlanService {
    return this.injector.get(PlanService);
  }

  private get stateLocationService(): StateLocationService {
    return this.injector.get(StateLocationService);
  }

  init(router: UIRouter) {
    this.shellService.showHeaderNav = true;

    this.appVersionService.initVersionCheck();
    this.storageService.set('baseurl', '', null);

    router.trace.enable('TRANSITION');
    // Scoped to the root/blank-page case only - a broad otherwise('/login') also catches the
    // intentionally-unmatched, GUID-suffixed URLs StateLocationService.stateChange() pushes for
    // browser-history bookkeeping, bouncing an already-successful transition back to login.
    router.urlService.rules.otherwise((matchValue, urlParts) => {
      if (urlParts.path === '' || urlParts.path === '/') {
        return '/login';
      }
    });
    router.transitionService.onStart({}, (trans) => this.onTransitionStart(router, trans));
    router.transitionService.onSuccess({}, (trans) => {
      // The user page opens on a skeleton, so a slow animated scroll reads as the page jumping.
      if (trans.to().name === 'missioncontrol.usermanagement') {
        window.scrollTo(0, 0);
      } else {
        $('html, body').animate({ scrollTop: 0 }, 'slow');
      }
      this.stateLocationService.stateChange();
    });
    this.listenForUrlChanges(router);
    router.transitionService.onError({}, (transition) => {
      console.log('state change error - ' + transition.error());
    });
    (window as any).myAppErrorLog = [];
    router.stateService.defaultErrorHandler((error) => {
      (window as any).myAppErrorLog.push(error);
      console.log('state change error - ', error);
    });
  }

  /**
   * Activates the state for the URL the page loaded on. The vanilla location service never
   * fires onChange for that URL, so a reload on a history URL has to be resolved here.
   */
  startRouter(router: UIRouter) {
    this.reconnectWebSocketOnRefresh(router);
    // History URLs match no state, so sync() alone would leave the page blank.
    if (this.stateLocationService.hasHistoryEntry(router.urlService.url())) {
      this.stateLocationService.locationChange();
    } else {
      router.urlService.sync();
    }
  }

  private listenForUrlChanges(router: UIRouter) {
    router.urlService.onChange(() => {
      this.stateLocationService.locationChange();
      this.modalService.closeAllOpenModals();
    });
  }

  private reconnectWebSocketOnRefresh(router: UIRouter) {
    const url = router.urlService.url();
    const isUserLoginRequiredScreen = !this.userService.isScreenWithLoginNotRequired(window.location.href);
    if (url !== '' && url !== '/' && isUserLoginRequiredScreen) {
      this.webSocketsService.connect();
    }
  }

  private onTransitionStart(router: UIRouter, trans: Transition) {
    this.shellService.showHeaderNav = true;
    if (trans.to().name === 'unreachable') {
      return false;
    }
    if (!LOGIN_NOT_REQUIRED_STATES.includes(trans.to().name)) {
      this.checkLoggedInUser(router);
    }
    if (trans.to().name === 'login') {
      // Must flip before the login view renders; flipping it in LoginPageComponent.ngOnInit
      // changes AppComponent's header *ngIf mid change-detection (ExpressionChanged error).
      this.authService.setUserActive(false);
      // A remembered user landing on login (e.g. via the back button) skips straight past it.
      this.redirectRememberedUser(router, trans);
    }
  }

  private checkLoggedInUser(router: UIRouter) {
    this.storageService.remove('wizardoperations');
    const userDetails = this.storageService.get('userDetails');
    if (!_.isNull(userDetails) && !_.isUndefined(userDetails) && !_.isUndefined(userDetails.accountState)) {
      if (userDetails.accountState === MESSAGECONSTANTS.USER_STATES.ACTIVE) {
        this.authService.setUserDetails(userDetails);
        this.authService.setUserActive(true);
        if (!_.isNull(userDetails.plan.message) && userDetails.plan.message.length > 0) {
          this.authService.userDetails.plan.message = this.planService.getPlanExpiryMessage(
            userDetails.plan.planType, moment(userDetails.plan.endDate), false);
          this.authService.notifyUserDetailsChanged();
        }
      }
    } else {
      console.log(this.storageService.get('baseurl'));
      console.log('redirecting to login');
      this.storageService.set('baseurl', '', null);
      router.stateService.go('login', null, { reload: true });
    }
  }

  private redirectRememberedUser(router: UIRouter, trans: Transition) {
    const rememberme = this.storageService.getCookie('rememberme');
    const userDetails = this.storageService.get('userDetails');
    if (!_.isUndefined(rememberme) && !_.isNull(rememberme) && !_.isUndefined(userDetails) && !_.isNull(userDetails)) {
      this.authService.setUserActive(true);
      this.authService.setUserDetails(this.storageService.get('userDetails'));
      this.webSocketsService.connect();
      if (trans.from().name === '') {
        const loginParamsObject = this.userService._setLoginParameters(router.urlService.search());
        this.userService.navigateToHomeScreen(loginParamsObject);
      }
    }
  }
}

/** Runs when Angular's UIRouter is first resolved, from the first `.run` in app.module.ajs.js. */
export function appInitConfig(router: UIRouter, injector: Injector) {
  injector.get(AppInitService).init(router);
}
